import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import UploadContextlyModal from "../components/UploadContextlyModal.js";
import "./ContextlyAI.css";
import { useTheme } from "../App";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const ContextlyAI = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSourcesCollapsed] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [sourceResponse, setSourceResponse] = useState({});
  const [isSourceGuideModalOpen, setIsSourceGuideModalOpen] = useState(false);
  const [isSourceGuideLoading, setIsSourceGuideLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [files, setFiles] = useState(location.state?.files || []);
  const [links, setLinks] = useState(location.state?.links || []);
  const [texts, setTexts] = useState(location.state?.texts || []);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const { theme, toggleTheme } = useTheme();

  const truncateName = (name, maxLength = 25) => {
    if (!name) return "";
    return name.length > maxLength ? name.slice(0, maxLength) + "..." : name;
  };



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getSourceNameById = (resourceId) => {
    const file = files.find((file) => file.resource_id === resourceId);
    if (file) return file.filename || "Unnamed File";
    const link = links.find((link) => link.resource_id === resourceId);
    if (link) return link.url || "Unnamed Link";
    const text = texts.find((text) => text.resource_id === resourceId);
    if (text) return text.filename || "Unnamed Text";
    return "Unknown Source";
  };

  useEffect(() => { scrollToBottom(); }, [chatMessages]);

  useEffect(() => {
    const timeout = setTimeout(() => { sessionStorage.clear(); }, 2 * 60 * 60 * 1000);
    return () => clearTimeout(timeout);
  }, []);

  const handleRedirect = async () => {
    const confirmRedirect = window.confirm("Are you sure you want to go back to the main page? This will delete your session.");
    if (!confirmRedirect) return;
    const sessionId = sessionStorage.getItem("currentSessionId");
    if (!sessionId) { alert("Session ID not found. Cannot delete session."); navigate("/"); return; }
    try {
      const response = await axios.delete(`${BASE_URL}/sessions/${sessionId}`);
      if (response.status === 200) { sessionStorage.clear(); navigate("/"); }
      else { alert("Failed to delete session. Please try again."); }
    } catch (error) {
      if (error.response?.status === 404) { sessionStorage.clear(); navigate("/"); }
      else { alert("Failed to delete session. Please try again."); }
    }
  };

  const generateCacheKey = (type, source) => {
    if (type === "File" && source.filename) return `source_${type}_${source.filename}`;
    if (type === "Link" && source.url) return `source_${type}_${source.url}`;
    if (type === "Text" && source.resource_id) return `source_${type}_${source.resource_id}`;
    return `source_${type}_unknown`;
  };

  const handleSourceClick = async (type, source) => {
    const cacheKey = `source_Guide_${source.resource_id}`;
    const cachedResponse = sessionStorage.getItem(cacheKey);
    setIsSourceGuideModalOpen(true);
    if (cachedResponse) { setSourceResponse(JSON.parse(cachedResponse)); setIsSourceGuideLoading(false); return; }
    setIsSourceGuideLoading(true);
    try {
      const sessionId = source.sessionId || sessionStorage.getItem("currentSessionId");
      const guideResponse = await axios.get(`${BASE_URL}/sessions/${sessionId}/resources/${source.resource_id}/source_guide`);
      const enrichedSource = { ...source, summary: guideResponse.data.summary, key_topics: guideResponse.data.key_topics };
      sessionStorage.setItem(cacheKey, JSON.stringify(enrichedSource));
      setSourceResponse(enrichedSource);
    } catch (error) {
      if (error.response && error.response.status === 429) {
        setSourceResponse({ 
          summary: `**Rate Limit Exceeded:** ${error.response.data.detail || "Please try again later."}`, 
          key_topics: [] 
        });
      } else {
        setSourceResponse({ summary: "Failed to retrieve source details. Please try again.", key_topics: [] });
      }
    } finally { setIsSourceGuideLoading(false); }
  };



  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    const userMessage = { type: "user", text: chatInput };
    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    try {
      const sessionId = sessionStorage.getItem("currentSessionId");
      if (!sessionId) throw new Error("Session ID not found.");
      const response = await axios.post(`${BASE_URL}/sessions/${sessionId}/chat`, { message: chatInput });
      const botMessage = { type: "bot", text: response.data.response || "I couldn't find anything useful.", references: response.data.sources || [] };
      setChatMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      if (error.response && error.response.status === 429) {
        setChatMessages((prev) => [...prev, { type: "bot", text: `**Rate Limit Exceeded:** ${error.response.data.detail || "Please try again later."}` }]);
      } else {
        setChatMessages((prev) => [...prev, { type: "bot", text: "Sorry, something went wrong. Please try again later." }]);
      }
    }
  };

  const closeSourceGuideModal = () => { setIsSourceGuideModalOpen(false); setSourceResponse({}); };

  const handleUploadSubmit = (organizedData) => {
    const { files: newFiles, links: newLinks, texts: newTexts } = organizedData;
    setFiles([...files, ...newFiles.filter((f) => !files.some((e) => e.resource_id === f.resource_id))]);
    setLinks([...links, ...newLinks.filter((l) => !links.some((e) => e.resource_id === l.resource_id))]);
    setTexts([...texts, ...newTexts.filter((t) => !texts.some((e) => e.resource_id === t.resource_id))]);
    setIsUploadModalOpen(false);
  };

  const handleDeleteSource = async (type, source) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete this ${type.toLowerCase()} source?`);
    if (!confirmDelete) return;
    try {
      const sessionId = source.sessionId || sessionStorage.getItem("currentSessionId");
      const resourceId = source.resource_id;
      if (!sessionId || !resourceId) throw new Error("Missing session or resource information.");
      await axios.delete(`${BASE_URL}/sessions/${sessionId}/resources/${resourceId}`);
      if (type === "Text") setTexts((prev) => prev.filter((t) => t.resource_id !== resourceId));
      else if (type === "Link") setLinks((prev) => prev.filter((l) => l.resource_id !== resourceId));
      else if (type === "File") setFiles((prev) => prev.filter((f) => f.resource_id !== resourceId));
      const cacheKey = generateCacheKey(type, source);
      sessionStorage.removeItem(cacheKey);
    } catch (error) { alert("Failed to delete source. Please try again."); }
  };

  const totalSources = files.length + links.length + texts.length;
  const maxSources = 15;
  const storagePercent = Math.min((totalSources / maxSources) * 100, 100);

  const getSourceIcon = (type) => {
    if (type === 'pdf') return <div className="source-type-icon pdf"><i className="fa fa-file-pdf"></i></div>;
    if (type === 'link') return <div className="source-type-icon web"><i className="fa fa-globe"></i></div>;
    return <div className="source-type-icon text"><i className="fa fa-file-alt"></i></div>;
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(() => {});
  };

  const getSourceTypeInfo = (source) => {
    if (source.url) return { type: 'Web Source', icon: 'fa-globe', badge: 'WEB', badgeClass: 'web' };
    if (source.filename && source.filename.endsWith('.txt')) return { type: 'Text', icon: 'fa-file-alt', badge: 'TXT', badgeClass: 'text' };
    return { type: 'PDF', icon: 'fa-file-pdf', badge: 'PDF', badgeClass: 'pdf' };
  };

  return (
    <div className={`workspace-container ${theme}`}>
      {/* Header */}
      <header className="workspace-header">
        <div className="workspace-header-left">
          <div className="workspace-logo" onClick={handleRedirect} title="Back to home">
            <div className="workspace-logo-icon">C</div>
          </div>
          <div className="workspace-header-info">
            <h1 className="workspace-title">Your Active Contextly Session</h1>
            <div className="workspace-meta">
              <span className="workspace-model"><span className="model-dot"></span>Model: Gemini 2.5 Flash</span>
            </div>
          </div>
        </div>
        <div className="workspace-header-right">
          <button className="workspace-header-btn" onClick={toggleTheme} aria-label="Toggle theme">
            <i className={`fa ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>
          <div className="workspace-avatar">
            <i className="fa fa-user"></i>
          </div>
        </div>
      </header>

      <div className="workspace-body">
        {/* Sidebar */}
        <aside className={`workspace-sidebar ${isSourcesCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <span className="sidebar-label">CONTEXT SOURCES</span>
            <span className="sidebar-badge">{totalSources} Active</span>
          </div>

          <button className="sidebar-add-btn" onClick={() => setIsUploadModalOpen(true)}>
            <i className="fa fa-plus"></i> {!isSourcesCollapsed && 'Add Source'}
          </button>

          <div className="sidebar-sources">
            {files.filter((f) => f.filename?.endsWith(".pdf")).map((file, index) => (
              <div key={`file-${index}`} className="sidebar-source-item" onClick={() => handleSourceClick("File", { ...file, sessionId: sessionStorage.getItem("currentSessionId") })}>
                {getSourceIcon('pdf')}
                {!isSourcesCollapsed && (
                  <div className="source-info">
                    <span className="source-name">{truncateName(file.filename || "Unnamed File")}</span>
                    <span className="source-meta"><span className="source-synced"><i className="fa fa-check-circle"></i> Synced</span></span>
                  </div>
                )}
                {!isSourcesCollapsed && (
                  <button className="source-delete" onClick={(e) => { e.stopPropagation(); handleDeleteSource("File", file); }}>
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            ))}

            {links.map((link, index) => {
              if (typeof link.url !== "string") return null;
              return (
                <div key={`link-${index}`} className="sidebar-source-item" onClick={() => handleSourceClick("Link", { ...link, sessionId: sessionStorage.getItem("currentSessionId") })}>
                  {getSourceIcon('link')}
                  {!isSourcesCollapsed && (
                    <div className="source-info">
                      <span className="source-name">{truncateName(link.url || "Unnamed Link")}</span>
                      <span className="source-meta"><span className="source-synced"><i className="fa fa-check-circle"></i> Synced</span></span>
                    </div>
                  )}
                  {!isSourcesCollapsed && (
                    <button className="source-delete" onClick={(e) => { e.stopPropagation(); handleDeleteSource("Link", link); }}>
                      <i className="fa fa-times"></i>
                    </button>
                  )}
                </div>
              );
            })}

            {texts.map((text, index) => (
              <div key={`text-${index}`} className="sidebar-source-item" onClick={() => handleSourceClick("Text", { ...text, sessionId: sessionStorage.getItem("currentSessionId") })}>
                {getSourceIcon('text')}
                {!isSourcesCollapsed && (
                  <div className="source-info">
                    <span className="source-name">{truncateName(text.resource_id || "Unnamed Text")}</span>
                    <span className="source-meta"><span className="source-synced"><i className="fa fa-check-circle"></i> Synced</span></span>
                  </div>
                )}
                {!isSourcesCollapsed && (
                  <button className="source-delete" onClick={(e) => { e.stopPropagation(); handleDeleteSource("Text", text); }}>
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Storage Indicator */}
          {!isSourcesCollapsed && (
            <div className="sidebar-storage">
              <div className="storage-header">
                <i className="fa fa-cloud"></i>
                <span>Sources Used</span>
              </div>
              <div className="storage-bar">
                <div className="storage-fill" style={{ width: `${storagePercent}%` }}></div>
              </div>
              <div className="storage-info">
                <span>{totalSources} used</span>
                <span>{maxSources} max</span>
              </div>
            </div>
          )}
        </aside>

        {/* Chat Area */}
        <main className={`workspace-chat ${isSourcesCollapsed ? 'expanded' : ''}`}>
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-empty">
                <div className="chat-empty-icon"><i className="fa fa-comments"></i></div>
                <h3>Start a conversation</h3>
                <p>Ask questions about your uploaded documents to get AI-powered insights.</p>
              </div>
            )}

            {chatMessages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.type === "user" ? "user" : "bot"}`}>
                {msg.type === "bot" && (
                  <div className="chat-avatar bot-avatar">
                    <div className="avatar-icon">C</div>
                  </div>
                )}
                <div className="chat-bubble">
                  {msg.type === "bot" ? (
                    <ReactMarkdown className="chat-text" components={{
                      a: ({ children, href, ...props }) => {
                        const referenceIndex = parseInt(children[0].replace(/[[\]]/g, "")) - 1;
                        const reference = msg.references && msg.references[referenceIndex] ? msg.references[referenceIndex].source_text || "No reference available" : "No reference available";
                        const resourceId = msg.references && msg.references[referenceIndex] ? msg.references[referenceIndex].name : null;
                        const sourceName = resourceId ? getSourceNameById(resourceId) : "Unknown Source";
                        return (
                          <span className="reference-badge tooltip">
                            {children}
                            <span className="tooltiptext">
                              <strong className="tooltip-title">Reference Text:</strong><br />{reference}<br />
                              <strong className="tooltip-title">Reference Source:</strong><br />{sourceName}
                            </span>
                          </span>
                        );
                      }
                    }}>
                      {msg.text.replace(/\[(\d+)\]/g, (match) => `[${match.slice(1, -1)}](#${match.slice(1, -1)})`)}
                    </ReactMarkdown>
                  ) : (
                    <ReactMarkdown className="chat-text">{msg.text}</ReactMarkdown>
                  )}
                  {msg.type === "user" && (
                    <span className="chat-time">You · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                </div>
                {msg.type === "bot" && (
                  <div className="chat-actions">
                    <button className="chat-action-btn" onClick={() => handleCopy(msg.text, index)}>
                      {copiedIndex === index ? <><i className="fa fa-check"></i> Copied!</> : <><i className="fa fa-copy"></i> Copy</>}
                    </button>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef}></div>
          </div>

          <div className="chat-input-area">
            <div className="chat-input-box">
              <input
                type="text"
                className="chat-input"
                placeholder="Ask a follow up question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && chatInput.trim()) handleSendMessage(); }}
              />
              <div className="chat-input-actions">
                <span className="chat-input-hint">SHIFT + ENTER FOR NEW LINE</span>
                <button className="chat-send-btn" onClick={handleSendMessage} disabled={!chatInput.trim()}>
                  <i className="fa fa-arrow-right"></i>
                </button>
              </div>
            </div>
            <p className="chat-disclaimer">Contextly AI can make mistakes. Consider checking important information.</p>
          </div>
        </main>
      </div>

      {/* Source Guide Modal - Executive Summary Style */}
      {isSourceGuideModalOpen && (
        <div className="exec-modal-overlay" onClick={closeSourceGuideModal}>
          <div className="exec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="exec-modal-header">
              <div className="exec-breadcrumb">
                <i className={`fa ${getSourceTypeInfo(sourceResponse).icon}`}></i>
                <span>Sources</span>
                <i className="fa fa-chevron-right"></i>
                <span>{sourceResponse.filename || sourceResponse.url || 'Source'}</span>
              </div>
              <div className="exec-header-actions">
                <button className="exec-header-btn" onClick={closeSourceGuideModal} aria-label="Close"><i className="fa fa-times"></i></button>
              </div>
            </div>

            <div className="exec-modal-body">
              {isSourceGuideLoading ? (
                <div className="exec-spinner"><div className="spinner"></div></div>
              ) : (
                <>
                  <div className="exec-doc-card">
                    <div className="exec-doc-info">
                      <span className={`exec-doc-badge ${getSourceTypeInfo(sourceResponse).badgeClass}`}>{getSourceTypeInfo(sourceResponse).badge}</span>
                    </div>
                    <h2 className="exec-doc-title">{sourceResponse.filename || sourceResponse.url || 'Source'}</h2>
                  </div>

                  <div className="exec-summary-section">
                    <h3 className="exec-section-title">
                      <i className="fa fa-magic"></i> Executive Summary
                    </h3>
                    <ReactMarkdown className="exec-summary-text">{sourceResponse.summary}</ReactMarkdown>
                  </div>

                  {sourceResponse.key_topics && sourceResponse.key_topics.length > 0 && (
                    <>
                      <div className="exec-topics-section">
                        <h3 className="exec-section-title">
                          <i className="fa fa-lightbulb"></i> KEY TOPICS
                        </h3>
                        <div className="exec-topics">
                          {sourceResponse.key_topics.map((topic, index) => (
                            <span
                              key={index}
                              className="exec-topic-tag"
                              onClick={() => {
                                closeSourceGuideModal();
                                const formattedTopic = `discuss ${topic.replace(/^\d+\.\s/, "").trim()}`;
                                const userMessage = { type: "user", text: formattedTopic };
                                setChatMessages((prev) => [...prev, userMessage]);
                                const sendMessage = async () => {
                                  try {
                                    const sessionId = sessionStorage.getItem("currentSessionId");
                                    if (!sessionId) throw new Error("Session ID not found.");
                                    const response = await axios.post(`${BASE_URL}/sessions/${sessionId}/chat`, { message: formattedTopic });
                                    setChatMessages((prev) => [...prev, { type: "bot", text: response.data.response || "I couldn't find anything useful.", references: response.data.sources || [] }]);
                                  } catch (error) {
                                    if (error.response && error.response.status === 429) {
                                      setChatMessages((prev) => [...prev, { type: "bot", text: `**Rate Limit Exceeded:** ${error.response.data.detail || "Please try again later."}` }]);
                                    } else {
                                      setChatMessages((prev) => [...prev, { type: "bot", text: "Sorry, something went wrong. Please try again later." }]);
                                    }
                                  }
                                };
                                sendMessage();
                              }}
                            >
                              <span className="topic-dot"></span>
                              {topic.replace(/^\d+\.\s/, "").trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="exec-modal-footer">
                    <button className="exec-link-btn">View Full Document <i className="fa fa-arrow-right"></i></button>
                    <div className="exec-footer-actions">
                      <button className="exec-action-btn secondary" onClick={closeSourceGuideModal}>Ask AI a Question</button>
                      <button className="exec-action-btn primary" onClick={closeSourceGuideModal}>Generate Full Report</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <UploadContextlyModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={handleUploadSubmit}
        existingSources={{
          files: files.map((file) => file.filename),
          links: links.map((link) => link.url),
          texts: texts.map((text) => text.filename),
        }}
      />
    </div>
  );
};

export default ContextlyAI;
